/**
 * TierOneImmunityExplainers — progressive-disclosure explainers for Tier 1 immunity.
 *
 * These two master theorems are the Tier 1 root of the Structural Safety
 * Mechanism family: the Categorical Immunity Theorem yields the ten
 * Neutralization Theorems, and the Purity Invariant (code PURITY) certifies
 * that no hidden generator has been smuggled into the pure cell type.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useId } from "react";
import {
  FONT,
  GRAY,
  MODE_COLOR,
  PRIMARY,
  SAFE,
  WARN,
  withAlpha,
} from "../../design-system/tokens.js";
import { Arrow, ArrowDefs, ExplainerCard, type ExplainerTierContent } from "../shared/index.js";
import {
  CATEGORICAL_IMMUNITY_ACCENT,
  CATEGORICAL_IMMUNITY_IDENTITY,
  CROSS_LINKS,
  DATA,
  GENERATOR_Y,
  MODE_TO_TOKEN,
  PURITY_ACCENT,
  PURITY_CROSS_LINKS,
  PURITY_IDENTITY,
  type PurityConstructor,
  type TierOneConsequence,
  type TierOneGenerator,
} from "./tierOneImmunityModel.js";

const CATEGORICAL_IMMUNITY_TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      <strong>Categorical Immunity Theorem</strong> says every <code>SecureGovernancePattern</code>{" "}
      instance makes its associated vulnerability class <em>structurally inexpressible</em>; the ten
      Neutralization Theorems are consequences of that single theorem.
    </p>
  ),
  intuition: (
    <>
      <p>
        Think of the Categorical Immunity Theorem as the cone point of the S1 family. The four
        generators identify the possible ways structure can go wrong; the theorem is the shared
        proof shape that sends each governed pattern to the Neutralization Theorem it instantiates.
      </p>
      <p>
        The SSM cards therefore do not tell ten independent stories. They show ten projections from
        one categorical statement: choose a SecureGovernancePattern, embed its local violation type,
        and recover the local neutralization from the Categorical Immunity Theorem.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        The master statement is the Agda term{" "}
        <code>categorical-immunity : (P : SecureGovernancePattern) → ¬ (SGPViolation P)</code>. The
        reusable recovery principle packages a theorem-specific projection and violation embedding
        as <code>CITConsequence</code>.
      </p>
      <CategoricalImmunityConeDiagram />
      <p style={{ marginTop: "0.5rem" }}>
        The Kernel-side integration module names the ten S1 results as direct consequences of the
        Categorical Immunity Theorem: each theorem provides an{" "}
        <code>AsSecureGovernancePattern</code> projection and an embedding of its local violation
        type into <code>SGPViolation</code>.
      </p>
    </>
  ),
  relevance: (
    <>
      <p>
        <strong>Why this page roots the SSM family.</strong> Every Neutralization Theorem card can
        point back here when it says its vulnerability class is made unconstructable by
        construction. The Categorical Immunity Theorem is the shared certificate; the local card
        explains the categorical structure that supplies the projection.
      </p>
      <p>
        <strong>Theorem-collision check.</strong> Treating one NT as an isolated guarantee collides
        with the Categorical Immunity Theorem: the local proof is supposed to factor through{" "}
        <code>CITConsequence</code>. Adding a hidden generator collides with the Purity Invariant
        (code <code>PURITY</code>), because the pure cell type admits no mixed constructor for the
        extra case.
      </p>
    </>
  ),
  references: [
    {
      label: "Theorem Canon (§2 and §8)",
      path: "docs/reference/Theorem-Canon.md",
    },
    {
      label: "Abstract Categorical Immunity Theorem proof",
      path: "agda/Foundation/SecureGovernancePattern/CategoricalImmunityTheorem.agda",
      note: "Canonical source for categorical-immunity, recover, and CITConsequence.",
    },
    {
      label: "Kernel Categorical Immunity Theorem consequence surface",
      path: "agda/Kernel/Integration/CITConsequences.agda",
      note: "Kernel-side integration of the ten Neutralization Theorems as direct consequences of the Categorical Immunity Theorem.",
    },
    {
      label: "Theory: minimal Categorical Immunity Theorem structure",
      path: "theory/structural/DOUBLE-CATEGORY-MINIMALITY-FOR-CIT.md",
    },
    {
      label: "Theory: master theorem hierarchy",
      path: "theory/structural/Master-Theorem-Hierarchy.md",
    },
  ],
};

const PURITY_TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      <strong>Purity Invariant</strong> (code <code>PURITY</code>) says the pure cell type has
      exactly the horizontal and vertical cell constructors needed by the kernel; a candidate hidden
      generator has no constructor.
    </p>
  ),
  intuition: (
    <>
      <p>
        The Purity Invariant is the pruning theorem behind the Tier 1 story. If someone proposes an
        extra hidden generator, the answer is not to add another runtime guard; the candidate simply
        has no place in <code>Cell₀</code>.
      </p>
      <p>
        The result is why the Categorical Immunity Theorem cone is complete at the S1 level. The
        pure cell grammar already accounts for the horizontal and vertical lanes. A mixed hidden
        lane would be a third constructor, and that constructor is absent.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        The proof proceeds by exhaustive case analysis on <code>Cell₀</code>. Each inhabitant is
        either <code>hor-cell</code> or <code>ver-cell</code>, and <code>no-mixed-cell</code> turns
        the attempted mixed case into contradiction.
      </p>
      <PurityPruningDiagram />
      <p style={{ marginTop: "0.5rem" }}>
        In the SecureGovernancePattern module this is packaged as <code>cell-pure</code>,{" "}
        <code>no-mixed-cell</code>, and preservation lemmas for horizontal and vertical cell
        composition. In DoubleCategoryTheory it appears as the Purity Invariant certificate.
      </p>
    </>
  ),
  relevance: (
    <>
      <p>
        <strong>Absence of hidden generators.</strong> The Purity Invariant is the certificate that
        the Categorical Immunity Theorem cone has no unseen branch feeding the S1 theorem family.
        That matters because a hidden generator would undermine the claim that the ten NTs are
        projections of a single master theorem.
      </p>
      <p>
        <strong>Theorem-collision check.</strong> Any proposed eleventh S1 generator must first
        explain why it is not a mixed-cell inhabitant. If it needs a new <code>Cell₀</code>{" "}
        constructor, it collides with the Purity Invariant and then with the SFDT-1 no-fifth-stratum
        result.
      </p>
    </>
  ),
  references: [
    {
      label: "Theorem Canon (§8 Tier 1)",
      path: "docs/reference/Theorem-Canon.md",
    },
    {
      label: "SecureGovernancePattern Purity Invariant proof",
      path: "agda/Foundation/SecureGovernancePattern/PurityInvariant.agda",
    },
    {
      label: "DoubleCategoryTheory Purity Invariant certificate",
      path: "agda/Foundation/DoubleCategoryTheory/PurityInvariant.agda",
    },
    {
      label: "Theory: square-firewall determinism",
      path: "theory/structural/SQUARE-FIREWALL-DETERMINISM.md",
    },
    {
      label: "Theory: plain-language master theorem guide",
      path: "theory/structural/Master-Theorem-Companion.md",
    },
  ],
};

export function CategoricalImmunityExplainer(): React.JSX.Element {
  return (
    <ExplainerCard
      identity={CATEGORICAL_IMMUNITY_IDENTITY}
      tiers={CATEGORICAL_IMMUNITY_TIERS}
      diagram={<CategoricalImmunityConeDiagram />}
      crossLinks={CROSS_LINKS}
    />
  );
}

export function PurityExplainer(): React.JSX.Element {
  return (
    <ExplainerCard
      identity={PURITY_IDENTITY}
      tiers={PURITY_TIERS}
      diagram={<PurityPruningDiagram />}
      crossLinks={PURITY_CROSS_LINKS}
    />
  );
}

export function CategoricalImmunityConeDiagram(): React.JSX.Element {
  const markerId = useId();
  return (
    <svg
      role="img"
      aria-label="Categorical Immunity Theorem cone: four generators G1 through G4 map through one master theorem to ten Neutralization Theorem consequences"
      viewBox="0 0 720 340"
      width="100%"
      height="auto"
      style={{ display: "block", maxWidth: "720px", margin: "0 auto" }}
    >
      <ArrowDefs id={markerId} color={CATEGORICAL_IMMUNITY_ACCENT} />
      <rect x="8" y="8" width="704" height="324" rx="8" fill="white" stroke={GRAY[300]} />
      <text x="360" y="31" textAnchor="middle" fontSize="14" fontWeight={700} fill={GRAY[900]}>
        Categorical Immunity Theorem cone: G1-G4 into the ten S1 consequences
      </text>
      {DATA.generators.map((generator) => (
        <GeneratorNode key={generator.id} generator={generator} />
      ))}
      {DATA.generators.map((generator) => (
        <Arrow
          key={`${generator.id}-to-categorical-immunity`}
          x1={150}
          y1={GENERATOR_Y[generator.id]}
          x2={295}
          y2={164}
          markerId={markerId}
          color={CATEGORICAL_IMMUNITY_ACCENT}
          variant="solid"
        />
      ))}
      <g>
        <circle
          cx="350"
          cy="164"
          r="44"
          fill={withAlpha(CATEGORICAL_IMMUNITY_ACCENT, "1A")}
          stroke={CATEGORICAL_IMMUNITY_ACCENT}
        />
        <text
          x="350"
          y="157"
          textAnchor="middle"
          fontSize="13"
          fontWeight={800}
          fill={CATEGORICAL_IMMUNITY_ACCENT}
          fontFamily={FONT.heading}
        >
          Categorical
        </text>
        <text
          x="350"
          y="178"
          textAnchor="middle"
          fontSize="13"
          fontWeight={800}
          fill={CATEGORICAL_IMMUNITY_ACCENT}
          fontFamily={FONT.heading}
        >
          Immunity
        </text>
        <text x="350" y="195" textAnchor="middle" fontSize="10" fill={GRAY[700]}>
          one master proof
        </text>
      </g>
      {DATA.consequences.map((consequence, index) => {
        const y = 52 + index * 27;
        return (
          <React.Fragment key={consequence.name}>
            <Arrow
              x1={395}
              y1={164}
              x2={492}
              y2={y}
              markerId={markerId}
              color={CATEGORICAL_IMMUNITY_ACCENT}
              variant="solid"
            />
            <ConsequenceNode consequence={consequence} y={y} />
          </React.Fragment>
        );
      })}
    </svg>
  );
}

export function PurityPruningDiagram(): React.JSX.Element {
  const [horizontal, vertical] = DATA.purity.constructors;
  const markerId = useId();
  return (
    <svg
      role="img"
      aria-label="Purity Invariant pruning: horizontal and vertical Cell0 constructors remain, while a candidate hidden generator is not in the cone"
      viewBox="0 0 720 260"
      width="100%"
      height="auto"
      style={{ display: "block", maxWidth: "720px", margin: "0 auto" }}
    >
      <ArrowDefs id={markerId} color={PURITY_ACCENT} />
      <rect x="8" y="8" width="704" height="244" rx="8" fill="white" stroke={GRAY[300]} />
      <text x="360" y="32" textAnchor="middle" fontSize="14" fontWeight={700} fill={GRAY[900]}>
        Purity Invariant pruning: no hidden generator constructor
      </text>
      {horizontal !== undefined && (
        <PurityCell x={94} y={96} constructor={horizontal} color={PRIMARY[700]} />
      )}
      {vertical !== undefined && (
        <PurityCell x={94} y={166} constructor={vertical} color={SAFE[700]} />
      )}
      <Arrow x1={205} y1={96} x2={304} y2={131} markerId={markerId} color={PURITY_ACCENT} />
      <Arrow x1={205} y1={166} x2={304} y2={131} markerId={markerId} color={PURITY_ACCENT} />
      <g>
        <rect
          x="304"
          y="91"
          width="128"
          height="80"
          rx="8"
          fill={withAlpha(PURITY_ACCENT, "1A")}
          stroke={PURITY_ACCENT}
        />
        <text
          x="368"
          y="122"
          textAnchor="middle"
          fontSize="20"
          fontWeight={800}
          fill={PURITY_ACCENT}
        >
          Cell0
        </text>
        <text x="368" y="145" textAnchor="middle" fontSize="11" fill={GRAY[700]}>
          exactly two constructors
        </text>
      </g>
      <Arrow
        x1={535}
        y1={131}
        x2={444}
        y2={131}
        markerId={markerId}
        color={WARN[700]}
        variant="dashed"
        forbidden
      />
      <g>
        <rect
          x="535"
          y="93"
          width="128"
          height="76"
          rx="8"
          fill={withAlpha(WARN[700], "12")}
          stroke={WARN[700]}
          strokeDasharray="6 4"
        />
        <text x="599" y="121" textAnchor="middle" fontSize="15" fontWeight={800} fill={WARN[700]}>
          {DATA.purity.excludedCandidate.name}
        </text>
        <text x="599" y="143" textAnchor="middle" fontSize="11" fill={GRAY[700]}>
          {DATA.purity.excludedCandidate.meaning}
        </text>
      </g>
      <text x="360" y="221" textAnchor="middle" fontSize="12" fill={GRAY[700]}>
        Candidate hidden generator is pruned because no mixed constructor exists.
      </text>
    </svg>
  );
}

function GeneratorNode({ generator }: { readonly generator: TierOneGenerator }): React.JSX.Element {
  const y = GENERATOR_Y[generator.id];
  return (
    <g>
      <rect
        x="34"
        y={y - 25}
        width="116"
        height="50"
        rx="8"
        fill={withAlpha(CATEGORICAL_IMMUNITY_ACCENT, "10")}
        stroke={CATEGORICAL_IMMUNITY_ACCENT}
      />
      <text
        x="92"
        y={y - 5}
        textAnchor="middle"
        fontSize="14"
        fontWeight={800}
        fill={CATEGORICAL_IMMUNITY_ACCENT}
      >
        {generator.id} {generator.name}
      </text>
      <text x="92" y={y + 13} textAnchor="middle" fontSize="10" fill={GRAY[700]}>
        {generator.principle}
      </text>
    </g>
  );
}

function ConsequenceNode({
  consequence,
  y,
}: {
  readonly consequence: TierOneConsequence;
  readonly y: number;
}): React.JSX.Element {
  const modeColor = MODE_COLOR[MODE_TO_TOKEN[consequence.mode]].dim;
  return (
    <g>
      <rect
        x="500"
        y={y - 14}
        width="172"
        height="24"
        rx="6"
        fill={withAlpha(modeColor, "12")}
        stroke={modeColor}
      />
      <text x="510" y={y + 2} fontSize="11" fontWeight={800} fill={GRAY[900]}>
        {consequence.code}
      </text>
      <text x="545" y={y + 2} fontSize="11" fontWeight={700} fill={GRAY[900]}>
        {consequence.name}
      </text>
      <text x="646" y={y + 2} fontSize="9" textAnchor="middle" fill={modeColor}>
        {consequence.mode}
      </text>
    </g>
  );
}

function PurityCell({
  x,
  y,
  constructor,
  color,
}: {
  readonly x: number;
  readonly y: number;
  readonly constructor: PurityConstructor;
  readonly color: string;
}): React.JSX.Element {
  return (
    <g>
      <rect
        x={x}
        y={y - 25}
        width="112"
        height="50"
        rx="8"
        fill={withAlpha(color, "12")}
        stroke={color}
      />
      <text x={x + 56} y={y - 3} textAnchor="middle" fontSize="14" fontWeight={800} fill={color}>
        {constructor.name}
      </text>
      <text x={x + 56} y={y + 15} textAnchor="middle" fontSize="10" fill={GRAY[700]}>
        {constructor.meaning}
      </text>
    </g>
  );
}
